import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { EditableInnerBoxTitle } from '@repo/ui/editable-box'
import { PaySlipIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Switch from '@repo/ui/switch'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { constructClientPath } from '../../../../../../../packages/toolkit/src/utils/functions/construct-path'
import {
  defaultEnrollmentStatus,
  getEnrollmentStatus
} from '../../../../api/enrollments/get-enrollment-status'
import { getPaymentRecords } from '../../../../api/enrollments/get-payment-records'
import { getPaymentRecordsStatusesStatistics } from '../../../../api/enrollments/get-payment-records-statistics'
import { payInstantInstallment } from '../../../../api/enrollments/pay-installment'
import {
  activateAutomaticPayments,
  deactivateAutomaticPayments
} from '../../../../api/enrollments/update-automatic-payments'
import { Paths } from '../../../../lib/constants/path'
import { useObserver } from '../../../../lib/contexts/observer'
import useEnrollment from '../../../../lib/hooks/use-enrollment'
import { PaymentStatusEnum } from '../../../../lib/types/enums/enrollment/payment-status.enum'
import { paymentMeansEnumMapping } from '../../../../lib/types/enums/payment-record/payment-means.enum'
import { PaymentTypeEnum } from '../../../../lib/types/enums/payment-record/payment-type.enum'
import { StatusEnum } from '../../../../lib/types/enums/payment-record/status.enum'
import { paymentRecordClientPaymentFields } from '../../../../lib/types/fields/payment-records/payment-record'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { formatCardNumber } from '../../../../lib/utils/functions/card-number.functions'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import EnrollmentProfileMonitoring from '../../enrollment-profile/enrollment-profile-monitoring'
import PaymentRecordStatusBadge from '../../enrollment-profile/shared/payment-record-status-badge'
import EnrollmentPaymentRecord from '../../enrollments/enrollment-form/enrollment-payment-record'
import EnrollmentPropositionSummary from '../../enrollments/enrollment-form/enrollment-proposition-summary'
import { OtoIndividualEnrollmentPriceDetails } from '../../enrollments/oto-individual-price-details'

const ClientPaymentInformation = () => {
  const [buttonClickedId, setButtonClickedId] = useState<number | null>(null)
  const { navigate } = useNavigator()
  const queryClient = useQueryClient()
  const { enrollment, setEnrollment } = useEnrollment()
  const observer = useObserver({ key: 'update-payment-status' })

  const {
    data: { hasPayment }
  } = useQuery({
    initialData: defaultEnrollmentStatus,
    queryKey: ['enrollment-status', enrollment.id],
    queryFn: () => getEnrollmentStatus(enrollment.id)
  })

  const { data: paymentRecordsStatistics } = useQuery({
    enabled: hasPayment,
    queryKey: ['paymentRecordsStatistics', enrollment.id],
    queryFn: () => getPaymentRecordsStatusesStatistics({ enrollmentId: enrollment.id })
  })

  const { data: paymentRecords } = useQuery({
    enabled: hasPayment,
    queryKey: ['payment-records', enrollment.id, paymentRecordClientPaymentFields],
    queryFn: () =>
      getPaymentRecords({ enrollmentId: enrollment.id }, paymentRecordClientPaymentFields)
  })

  const {
    mutate: activateAutomaticPaymentsMutation,
    isPending: isActivateAutomaticPaymentsPending
  } = useMutation({
    mutationFn: () => activateAutomaticPayments({ enrollmentId: enrollment.id }),
    onError: () => {
      setEnrollment({ ...enrollment, optinForAutomaticDebit: false })
    }
  })

  const { mutate: payInstallmentMutation, isPending: isPayInstallment } = useMutation({
    mutationFn: payInstantInstallment,
    onError: (error) => {
      setButtonClickedId(null)
      showAxiosError(error)
    },
    onSuccess: () => {
      setButtonClickedId(null)
      toast.success('Inscription confirmée')
      queryClient.invalidateQueries({ queryKey: ['paymentRecordsStatistics', enrollment.id] })
      queryClient.invalidateQueries({ queryKey: ['payment-records', enrollment.id] })
      observer.notify(['enrollment-client-personal-space'])
    }
  })

  const {
    mutate: deactivateAutomaticPaymentsMutation,
    isPending: isDeactivateAutomaticPaymentsPending
  } = useMutation({
    mutationFn: () => deactivateAutomaticPayments({ enrollmentId: enrollment.id }),
    onError: () => {
      setEnrollment({ ...enrollment, optinForAutomaticDebit: true })
    }
  })
  const isAllPaymentsFinished = enrollment.paymentStatus === PaymentStatusEnum.FULLY_PAID
  // a cancelled stay has no balance left to pay
  const isCancelled = EnrollmentProperties.isCancelled(enrollment)

  if (!paymentRecordsStatistics || !paymentRecords) return <Loader center />

  return (
    <InnerBox className="rounded-lg p-4 mobile:px-1.5 mobile:py-3">
      <div className="flex items-center justify-between mobile:flex-col mobile:items-start mobile:gap-2">
        <div className="flex items-center gap-1.5">
          <Badge
            icon={<PaySlipIcon color={tailwindColors.primary.DEFAULT} />}
            variant="primary"
            size="flexible"
            className="rounded"
          />
          <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
            Suivi administratif et comptable
          </Typography>
        </div>
        {!isAllPaymentsFinished && !isCancelled && hasPayment && (
          <Button
            label="Payer le solde"
            variant="destructive"
            onClick={() =>
              navigate(
                constructClientPath(Paths.CLIENT_PERSONAL_SPACE_FINALIZE_PAYMENT, enrollment.id)
              )
            }
          />
        )}
      </div>
      <div className="h-3" />
      <InnerBox className="flex flex-row bg-white-hover rounded-lg justify-between gap-4 mobile:gap-3 mobile:flex-col mobile:items-start">
        {hasPayment && (
          <div className="flex-1  mobile:w-full">
            <EditableInnerBoxTitle title="Détails des paiements" />
            {!isAllPaymentsFinished ? (
              <EnrollmentPaymentRecord.PaymentSummary enrollmentId={enrollment.id} mode="client" />
            ) : (
              <Badge
                variant="success"
                text={`Séjour totalement payé : ${paymentRecordsStatistics?.totalAmount} €`}
              />
            )}
            <div className="h-2" />
            <div>
              <Typography variant="tag" className="text-grey">
                Paiements effectués
              </Typography>
              <div className="h-1" />
              <EnrollmentProfileMonitoring.PaymentTable
                type="finished"
                columns={[
                  {
                    accessorKey: 'means',
                    header: 'Moyen de paiement',
                    cell: ({ row }) => paymentMeansEnumMapping[row.original.means]
                  },
                  {
                    accessorKey: 'status',
                    header: 'Statut',
                    cell: ({ row }) => <PaymentRecordStatusBadge paymentRecord={row.original} />
                  }
                ]}
                records={paymentRecords.data.filter(
                  (record) =>
                    record.status !== StatusEnum.PENDING &&
                    record.type !== PaymentTypeEnum.BANK_IMPRINT
                )}
              />
            </div>
            <div className="h-2" />

            {!isAllPaymentsFinished && (
              <>
                <div className="flex flex-col items-start gap-2">
                  <Typography variant="tag" className="text-grey">
                    Echéances de paiement
                  </Typography>
                  <InnerBox className="bg-primary-light p-2 rounded-sm  border-primary-light">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={enrollment.optinForAutomaticDebit}
                        isLoading={
                          isActivateAutomaticPaymentsPending ||
                          isDeactivateAutomaticPaymentsPending
                        }
                        onCheckedChange={() => {
                          const newValue = !enrollment.optinForAutomaticDebit
                          setEnrollment({ ...enrollment, optinForAutomaticDebit: newValue })
                          if (newValue) activateAutomaticPaymentsMutation()
                          else deactivateAutomaticPaymentsMutation()
                        }}
                      />
                      <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
                        Prélèvement automatique
                      </Typography>
                    </div>
                    <div className="h-1" />
                    <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
                      Les échéances seront prélevées automatiquement aux dates indiquées. Vous avez
                      la possibilité de payer en avance si vous le souhaitez.
                    </Typography>
                  </InnerBox>
                </div>

                <div className="h-2" />
                <EnrollmentProfileMonitoring.PaymentTable
                  type="pending"
                  records={paymentRecords.data.filter(
                    (record) =>
                      record.status === StatusEnum.PENDING &&
                      record.type === PaymentTypeEnum.PAYMENT
                  )}
                  columns={[
                    {
                      accessorKey: 'cardNumber',
                      header: 'Carte',
                      cell: ({ row }) =>
                        valueRender(row.original.cardNumber, (cardNumber) =>
                          formatCardNumber(cardNumber)
                        )
                    },
                    {
                      accessorKey: 'id',
                      header: '',
                      cell: ({ row }) => (
                        <div className="flex gap-2 items-center">
                          <Button
                            label="Payer par carte"
                            variant="primary"
                            loading={isPayInstallment && buttonClickedId === row.original.id}
                            onClick={(e) => {
                              e.preventDefault()
                              setButtonClickedId(row.original.id)
                              payInstallmentMutation({
                                id: enrollment.id,
                                paymentRecordId: row.original.id
                              })
                            }}
                          />
                        </div>
                      )
                    }
                  ]}
                />
              </>
            )}
          </div>
        )}
        <div className={cn({ 'w-96 mobile:w-full': hasPayment, 'w-full': !hasPayment })}>
          {/* the pricing detail is read from the journey proposition; a One to One Individuel
              enrollment is priced from its own proposition instead */}
          {EnrollmentProperties.isOtoIndividual(enrollment) ? (
            <OtoIndividualEnrollmentPriceDetails
              enrollmentId={enrollment.id}
              title={<EditableInnerBoxTitle title="Détail du tarif (tout inclus)" />}
              className="bg-white-hover"
              fallbackOptionTitle={
                getTranslatedName(enrollment.otoIndividualProposition?.option) || undefined
              }
            />
          ) : (
            enrollment.mainProposition && (
              <EnrollmentPropositionSummary
                title={<EditableInnerBoxTitle title="Détail du tarif (tout inclus)" />}
                readonly
                enrollmentId={enrollment.id}
                propositionId={enrollment.mainProposition.id}
                className="bg-white-hover"
                onInputChange={async () => {}}
              />
            )
          )}
        </div>
      </InnerBox>
    </InnerBox>
  )
}

export default ClientPaymentInformation
